import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HelpModal from '../components/HelpModal';
import { AnalyticsEvents } from '../utils/analytics';
import useClarityPageReady from '../hooks/useClarityPageReady';
import {
  BookingStepper,
  TripSummaryCard,
  VehicleTabs,
  VehicleDetailCard,
  PriceSummaryCard,
  NoVehiclesAvailable,
  MobilePriceSummary,
} from '../components/quote-v2';
import {
  filterVehiclesByCapacity,
  hasAvailableVehicles,
  canVehicleAccommodate,
} from '../config/vehicleTypes';
import { ArrowLeft, Users, Luggage, Minus, Plus, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

// Vehicle tab configuration
const VEHICLE_TAB_CONFIG = [
  { id: 'VAN', tabLabel: 'VAN' },
  { id: 'MINI', tabLabel: 'MINI' },
  { id: 'MIDI', tabLabel: 'MIDI' },
  { id: 'BUS', tabLabel: 'BUS' },
];

// Format date for display - directly from raw strings (no timezone conversion)
const formatDateForDisplay = (dateStr, timeStr, timezone = 'Europe/Istanbul') => {
  if (!dateStr) return 'Date not set';
  
  try {
    // Parse date string (YYYY-MM-DD)
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // Create date object just to get weekday name
    const date = new Date(year, month - 1, day);
    
    // Get weekday and month names
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
    const monthName = date.toLocaleDateString('en-GB', { month: 'short' });
    
    // Format time as provided (no conversion)
    const time = timeStr || '00:00';
    
    // Return formatted string: "Sat, 31 Jan 2026 at 20:53"
    return `${weekday}, ${day} ${monthName} ${year} at ${time}`;
  } catch (e) {
    console.error('Date format error:', e);
    return `${dateStr} ${timeStr || ''}`;
  }
};

const QuotePageV2 = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [vehiclesFromApi, setVehiclesFromApi] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [activeTab, setActiveTab] = useState('VAN');

  // Clarity page ready signal - fires after UI is painted
  useClarityPageReady('vehicle_selection');

  // Get session ID from URL
  const sessionId = searchParams.get('sid') || searchParams.get('session_id');

  // Fetch session data from backend
  const fetchSession = useCallback(async () => {
    if (!sessionId) {
      setSessionError('No session ID provided');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setSessionError(null);

      // Fetch session details
      const sessionRes = await fetch(`${API_BASE}/api/booking/${sessionId}`);
      const sessionResult = await sessionRes.json();

      if (!sessionResult.success || !sessionResult.session) {
        setSessionError('Session not found or expired');
        setIsLoading(false);
        return;
      }

      const session = sessionResult.session;
      setSessionData(session);

      // Log for debugging
      console.log('[QuotePage] Session loaded:', {
        trip_date: session.trip_date,
        trip_time: session.trip_time,
        pickup: session.pickup_location,
        dropoff: session.dropoff_location
      });

      // Get vehicles from session snapshot or fetch fresh
      if (session.zuppin_pricing_snapshot && session.zuppin_pricing_snapshot.length > 0) {
        setVehiclesFromApi(session.zuppin_pricing_snapshot);
      } else {
        // Fetch vehicles separately if not in session
        const vehiclesRes = await fetch(`${API_BASE}/api/booking/${sessionId}/vehicles`);
        const vehiclesResult = await vehiclesRes.json();
        
        if (vehiclesResult.success && vehiclesResult.vehicles) {
          setVehiclesFromApi(vehiclesResult.vehicles);
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Failed to fetch session:', error);
      setSessionError('Failed to load booking details');
      setIsLoading(false);
    }
  }, [sessionId]);

  // Load session on mount
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Fire begin_quote analytics event
  useEffect(() => {
    if (sessionData) {
      AnalyticsEvents.beginQuote({
        pickup: sessionData.pickup_location,
        dropoff: sessionData.dropoff_location,
        passengers: sessionData.passengers,
      });
    }
  }, [sessionData]);

  // Build trip details from session data
  const tripDetails = useMemo(() => {
    if (!sessionData) {
      return {
        mode: 'transfer',
        tripType: 'one-way',
        pickup: '',
        dropoff: '',
        date: '',
        time: '',
        passengers: 1,
        luggage: 1,
        returnDate: '',
        returnTime: '',
        timezone: 'Europe/Istanbul',
        formattedDate: 'Loading...',
        pickupType: 'manual',
      };
    }

    const date = sessionData.trip_date || sessionData.pickup_date || '';
    const time = sessionData.trip_time || sessionData.pickup_time || '';
    const timezone = sessionData.timezone || 'Europe/Istanbul';

    return {
      mode: sessionData.trip_mode || 'transfer',
      tripType: sessionData.trip_type || 'one-way',
      pickup: sessionData.pickup_location || '',
      dropoff: sessionData.dropoff_location || '',
      date: date,
      time: time,
      passengers: sessionData.passengers || 1,
      luggage: sessionData.luggage || sessionData.passengers || 1,
      returnDate: sessionData.return_date || '',
      returnTime: sessionData.return_time || '',
      timezone: timezone,
      // Pre-formatted date string for display
      formattedDate: formatDateForDisplay(date, time, timezone),
      // Raw values for debugging
      rawDate: date,
      rawTime: time,
      pickupType: sessionData.pickup_type || 'manual',
    };
  }, [sessionData]);

  // Map API vehicles to our format
  const availableVehicles = useMemo(() => {
    // Don't use fallback vehicles while loading - wait for API data
    if (isLoading) {
      return [];
    }
    
    if (vehiclesFromApi.length > 0) {
      // Map Zuppin vehicle types/categories to our tab IDs
      const vehicleTypeMapping = {
        'MINIVAN': 'VAN',
        'MINIBUS': 'MINI',
        'MIDIBUS': 'MIDI',
        'BUS': 'BUS',
        'van': 'VAN',
        'minivan': 'VAN',
        'minibus': 'MINI',
        'midibus': 'MIDI',
        'bus': 'BUS',
        0: 'VAN',
        1: 'MINI',
        2: 'MIDI',
        3: 'BUS',
      };

      return vehiclesFromApi.map((v, index) => {
        // Get proper tab ID from mapping - check category first, then type
        const typeKey = v.category || v.type || v.vehicleType || v.name?.toLowerCase();
        const tabId = vehicleTypeMapping[typeKey] || vehicleTypeMapping[typeKey?.toUpperCase()] || `VEHICLE_${index}`;
        
        // Get capacity values - handle both old and new field names
        const passengers = v.capacity || v.maxPassengers || 6;
        const luggage = v.luggage_capacity || v.maxLuggage || 6;
        
        // Use English description based on category
        const englishDescriptions = {
          'VAN': `${passengers} passengers, ${luggage} luggage capacity`,
          'MINI': `${passengers} passengers, ${luggage} luggage capacity`,
          'MIDI': `${passengers} passengers, ${luggage} luggage capacity`,
          'BUS': `${passengers} passengers, ${luggage} luggage capacity`,
        };

        // English features (not Turkish)
        const englishFeatures = ['WiFi', 'Air Conditioning', 'Luggage Space', 'Professional Driver'];

        return {
          id: `${tabId}_${v.id || index}`, // Unique ID for each vehicle
          zuppinId: v.zuppinId || v.tripOptionsId || v.id,
          name: v.categoryDisplayName || v.name || 'VIP Vehicle',
          description: englishDescriptions[tabId] || `${passengers} passengers, ${luggage} luggage`,
          maxPassengers: passengers,
          maxLuggage: luggage,
          price: v.price || v.displayPrice || v.basePriceEUR || 0,
          currency: 'EUR',
          image: v.image || '',
          features: englishFeatures,
          tabLabel: tabId,
          category: v.category || tabId,
          rawData: v, // Keep original data for debugging
        };
      });
    }

    // Fallback to filtered vehicles ONLY if API returned no data (not while loading)
    if (!isLoading && vehiclesFromApi.length === 0) {
      const filtered = filterVehiclesByCapacity(tripDetails.passengers, tripDetails.luggage);
      return filtered.map((vehicle, index) => ({
        ...vehicle,
        price: Math.round(45 * (1 + index * 0.4)),
        currency: 'EUR',
        tabLabel: vehicle.id,
      }));
    }
    
    return [];
  }, [vehiclesFromApi, tripDetails.passengers, tripDetails.luggage, isLoading]);

  // Check if we have any available vehicles
  const hasVehicles = useMemo(() => {
    if (vehiclesFromApi.length > 0) return true;
    return hasAvailableVehicles(tripDetails.passengers, tripDetails.luggage);
  }, [vehiclesFromApi, tripDetails.passengers, tripDetails.luggage]);

  // Get available tabs - show tabs that have matching vehicles
  const availableTabs = useMemo(() => {
    // Get unique tabLabel values from available vehicles
    const availableTabLabels = [...new Set(availableVehicles.map(v => v.tabLabel))];
    return VEHICLE_TAB_CONFIG.filter(tab => availableTabLabels.includes(tab.id));
  }, [availableVehicles]);

  // Current vehicle based on active tab
  const currentVehicle = useMemo(() => {
    return availableVehicles.find(v => v.tabLabel === activeTab) || availableVehicles[0];
  }, [availableVehicles, activeTab]);

  // Set default active tab when vehicles change
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(t => t.id === activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  // Auto-select first available vehicle when vehicles load or tab changes
  useEffect(() => {
    if (availableVehicles.length > 0 && !selectedVehicle) {
      const firstVehicle = availableVehicles.find(v => v.tabLabel === activeTab) || availableVehicles[0];
      if (firstVehicle) {
        setSelectedVehicle(firstVehicle);
      }
    }
  }, [availableVehicles, activeTab, selectedVehicle]);

  // Update selected vehicle when tab changes
  useEffect(() => {
    if (availableVehicles.length > 0) {
      const vehicleForTab = availableVehicles.find(v => v.tabLabel === activeTab);
      if (vehicleForTab) {
        setSelectedVehicle(vehicleForTab);
      }
    }
  }, [activeTab, availableVehicles]);

  // Fire view_vehicle_step when vehicles are loaded
  useEffect(() => {
    if (!isLoading && availableVehicles.length > 0 && sessionData) {
      AnalyticsEvents.viewVehicleStep({
        vehicleCount: availableVehicles.length,
        pickup: sessionData.pickup_location,
        dropoff: sessionData.dropoff_location,
        passengers: sessionData.passengers,
        luggage: sessionData.luggage,
      });
    }
  }, [isLoading, availableVehicles, sessionData]);

  const handleSelectVehicle = (vehicle) => {
    if (!canVehicleAccommodate(vehicle, tripDetails.passengers, tripDetails.luggage)) {
      return;
    }
    setSelectedVehicle(vehicle);
    
    // Fire analytics events: select_vehicle + view_item (GA4 ecommerce)
    AnalyticsEvents.selectVehicle({
      vehicleType: vehicle.id,
      vehicleName: vehicle.name,
      price: vehicle.price,
      currency: 'EUR',
      passengers: tripDetails.passengers,
    });
    AnalyticsEvents.viewItem({
      vehicleType: vehicle.id,
      vehicleName: vehicle.name,
      price: vehicle.price,
      route: `${tripDetails.pickup} -> ${tripDetails.dropoff}`,
    });
  };

  const handleContinue = async () => {
    if (!selectedVehicle || !sessionId) return;

    try {
      // Save vehicle selection to backend
      const response = await fetch(`${API_BASE}/api/booking/vehicle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId,
          vehicleId: String(selectedVehicle.zuppinId || selectedVehicle.id),
          vehicleName: selectedVehicle.name,
          price: selectedVehicle.price,
          zuppinId: String(selectedVehicle.zuppinId || selectedVehicle.id)
        })
      });

      const result = await response.json();

      if (result.success) {
        // Fire analytics: add_to_cart (GA4 ecommerce) + continue_to_details
        AnalyticsEvents.addToCart({
          vehicleType: selectedVehicle.id,
          vehicleName: selectedVehicle.name,
          price: selectedVehicle.price,
          route: `${tripDetails.pickup} -> ${tripDetails.dropoff}`,
        });
        AnalyticsEvents.continueToDetails({
          vehicleType: selectedVehicle.id,
          price: selectedVehicle.price,
          sessionId: sessionId,
        });
        
        // Navigate to extras page with session ID
        navigate(`/extras?sid=${sessionId}`);
      } else {
        toast.error(result.error || 'Failed to select vehicle');
      }
    } catch (error) {
      console.error('Vehicle selection error:', error);
      toast.error('Connection error. Please try again.');
    }
  };

  const handlePassengerChange = (delta) => {
    const newCount = Math.max(1, Math.min(50, tripDetails.passengers + delta));
    // Note: In a real implementation, this would need to re-fetch pricing
    // For now, just update URL params
    const params = new URLSearchParams(searchParams);
    params.set('passengers', newCount.toString());
    if (tripDetails.luggage === tripDetails.passengers) {
      params.set('luggage', newCount.toString());
    }
    setSearchParams(params);
  };

  const handleLuggageChange = (delta) => {
    const newCount = Math.max(0, Math.min(50, tripDetails.luggage + delta));
    const params = new URLSearchParams(searchParams);
    params.set('luggage', newCount.toString());
    setSearchParams(params);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const vehicle = availableVehicles.find(v => v.id === tabId);
    if (vehicle) {
      setSelectedVehicle(vehicle);
    }
  };

  // Error state
  if (sessionError) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="pt-28 md:pt-32 pb-32 lg:pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Session Error</h2>
              <p className="text-gray-400 mb-6">{sessionError}</p>
              <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-600">
                Start New Booking
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* noindex for booking flow pages */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      
      <Header />

      <main className="pt-28 md:pt-32 pb-32 lg:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="mb-4">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="text-white border-white/30 hover:bg-white/10 hover:text-white bg-white/5"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Search
            </Button>
          </div>

          {/* Booking Stepper */}
          <BookingStepper currentStep={1} />

          {/* Passenger/Luggage Quick Edit Bar */}
          <div className="bg-white/5 rounded-xl p-4 mb-6 flex flex-wrap items-center gap-4 lg:gap-8">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-[#FF8C00]" />
              <span className="text-sm text-gray-300">Passengers</span>
              <div className="flex items-center bg-black/30 rounded-lg">
                <button
                  onClick={() => handlePassengerChange(-1)}
                  className="p-2 text-gray-400 hover:text-white disabled:opacity-50"
                  disabled={tripDetails.passengers <= 1}
                  data-testid="passenger-decrease"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-8 text-center font-semibold">{tripDetails.passengers}</span>
                <button
                  onClick={() => handlePassengerChange(1)}
                  className="p-2 text-gray-400 hover:text-white disabled:opacity-50"
                  disabled={tripDetails.passengers >= 50}
                  data-testid="passenger-increase"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Luggage className="h-5 w-5 text-[#FF8C00]" />
              <span className="text-sm text-gray-300">Luggage</span>
              <div className="flex items-center bg-black/30 rounded-lg">
                <button
                  onClick={() => handleLuggageChange(-1)}
                  className="p-2 text-gray-400 hover:text-white disabled:opacity-50"
                  disabled={tripDetails.luggage <= 0}
                  data-testid="luggage-decrease"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-8 text-center font-semibold">{tripDetails.luggage}</span>
                <button
                  onClick={() => handleLuggageChange(1)}
                  className="p-2 text-gray-400 hover:text-white disabled:opacity-50"
                  disabled={tripDetails.luggage >= 50}
                  data-testid="luggage-increase"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Main 3-Column Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: Trip Summary - Mobile: Show above vehicles */}
            <div className="lg:col-span-3 order-first lg:order-none">
              <div className="lg:sticky lg:top-28">
                <TripSummaryCard tripDetails={tripDetails} />
              </div>
            </div>

            {/* Center: Vehicle Selection */}
            <div className="lg:col-span-6">
              {isLoading ? (
                <div 
                  className="bg-black/50 backdrop-blur-sm rounded-xl p-12 text-center"
                  data-clarity-mask="true"
                >
                  <Loader2 className="h-8 w-8 animate-spin text-[#FF8C00] mx-auto mb-4" />
                  <p className="text-white/80">Finding available vehicles...</p>
                </div>
              ) : !hasVehicles ? (
                <NoVehiclesAvailable
                  passengerCount={tripDetails.passengers}
                  luggageCount={tripDetails.luggage}
                  onEditPassengers={() => handlePassengerChange(-1)}
                  onEditLuggage={() => handleLuggageChange(-1)}
                />
              ) : (
                <>
                  {/* Vehicle Tabs */}
                  <VehicleTabs
                    vehicles={availableTabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                  />

                  {/* Vehicle Detail Card */}
                  {currentVehicle && (
                    <VehicleDetailCard
                      vehicle={currentVehicle}
                      isSelected={selectedVehicle?.id === currentVehicle.id}
                      onSelect={handleSelectVehicle}
                      passengerCount={tripDetails.passengers}
                      luggageCount={tripDetails.luggage}
                    />
                  )}

                  {/* Vehicle count info */}
                  <p className="text-sm text-gray-400 mt-4 text-center">
                    {availableVehicles.length} vehicle{availableVehicles.length !== 1 ? 's' : ''} available
                    for {tripDetails.passengers} passenger{tripDetails.passengers !== 1 ? 's' : ''} and {tripDetails.luggage} luggage
                  </p>
                </>
              )}
            </div>

            {/* Right: Price Summary */}
            <div className="lg:col-span-3 hidden lg:block">
              <div className="sticky top-28">
                <PriceSummaryCard
                  selectedVehicle={selectedVehicle}
                  tripDetails={tripDetails}
                  onContinue={handleContinue}
                  availableVehicleCount={availableVehicles.length}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Price Summary */}
      <MobilePriceSummary
        selectedVehicle={selectedVehicle}
        tripDetails={tripDetails}
        onContinue={handleContinue}
      />

      <Footer />
      
      {/* Help Modal - shows on exit intent (replaces discount popup) */}
      <HelpModal 
        pagePath="/quote"
        pickupAirport={tripDetails.pickup}
        destination={tripDetails.dropoff}
        date={tripDetails.date}
        time={tripDetails.time}
        passengers={tripDetails.passengers}
        luggage={tripDetails.luggage}
      />
    </div>
  );
};

export default QuotePageV2;
